import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useLocation, useParams } from 'react-router-dom';
import * as events from '../../lib/analytics/events';
import { logInternalError } from '../../lib/errors';
import { applyReferralCodeToCart, isValidCodeResult } from '../../lib/promoCode';
import setReferralCookie from '../../lib/setReferralCookie';
import { cartActions as storeCartActions } from '../../reducers/storeCart';
import * as types from '../../types';
import { ReferralSource } from '../../types/gql-op-types';
import Loading from '../Loading';

function parseReferralSource(sourceParam: string | null): ReferralSource {
  switch (sourceParam) {
    case 'onboarding':
      return ReferralSource.Onboarding;
    case 'referrals_tab':
      return ReferralSource.ReferralsTab;
    default:
      return ReferralSource.DirectLink;
  }
}

export default function ReferralRedirect() {
  const cart = useSelector((state: types.AppState) => state.storeShop.cart);
  const dispatch = useDispatch();
  const location = useLocation();
  const { code } = useParams<{ code: string }>();
  const [loading, setLoading] = useState(true);

  const searchParams = new URLSearchParams(location.search);
  const redirectToMiniCats = searchParams.get('type') === 'cat';
  const source = parseReferralSource(searchParams.get('source'));

  useEffect(() => {
    async function applyReferralCode() {
      try {
        const result = await applyReferralCodeToCart(cart, code);

        // When auto applying referral code, we only don't want to do it if it is not a valid code.
        // If it's other errors (e.g. "Not applicable to any items in cart") then we still want to apply it.
        if (isValidCodeResult(result)) {
          let referrerName = undefined;
          if (result.kind === 'success') {
            referrerName = result.value.referrerName;
          }
          dispatch(storeCartActions.addReferral({ code, referrerName, source }));
          setReferralCookie(code, referrerName, source);
        }
      } catch (err) {
        logInternalError(err);
      } finally {
        setLoading(false);
      }
    }

    events.referralRedirect.loaded(code);
    applyReferralCode();
  }, [cart, code, dispatch, source]);

  if (loading) {
    return <Loading />;
  }

  const redirectUrl = redirectToMiniCats ? '/products/fi-mini?type=cat' : '/';
  return <Redirect to={`${redirectUrl}${location.search}`} />;
}
